import {
  frameAncestorsSourceLists,
  isFramingAllowed,
} from 'util/csp-frame-ancestors';
import { getEnv } from 'util/getEnv';

// Sentry's CSP report endpoint for the web-ordering-csp-reporting project.
// The key is the project's public key, the same one in the CSP report-uri.
const CSP_REPORT_ENDPOINT =
  'https://o4511167351029760.ingest.us.sentry.io/api/4512011870404608/security/?sentry_key=375769ee35a29765943cf6f172a22e74';

const STORAGE_KEY_PREFIX = 'inc-framed-by:';

const originOf = url => {
  try {
    return new URL(url).origin;
  } catch {
    return undefined;
  }
};

// Returns true if this origin has not yet been checked today from this
// browser, and records it. Storage can be unavailable or throw in a framed
// (third-party) context; in that case check anyway.
const isFirstReportToday = (storage, parentOrigin, today) => {
  try {
    const key = STORAGE_KEY_PREFIX + parentOrigin;
    if (storage.getItem(key) === today) return false;
    storage.setItem(key, today);
  } catch {
    // fall through and report
  }
  return true;
};

const errorMessage = error =>
  `${error?.name ?? 'Error'}: ${error?.message ?? String(error)}`;

// pagehide and pageshow are fired on the window; freeze, resume and
// visibilitychange are fired on the document (freeze and resume don't bubble,
// so a window listener would never see them).
const WINDOW_LIFECYCLE_EVENTS = ['pagehide', 'pageshow'];
const DOCUMENT_LIFECYCLE_EVENTS = ['freeze', 'resume', 'visibilitychange'];

// Records page lifecycle events from now on, with their time relative to
// `start`. Used to tell why a HEAD request failed: the page being torn down
// or navigated (pagehide), parked in and restored from the back/forward cache
// (pageshow persisted), frozen in the background, or none of those.
const watchLifecycle = (win, start) => {
  const events = [];
  const record = event => {
    const persisted = event.persisted ? ' persisted' : '';
    const hidden =
      event.type === 'visibilitychange'
        ? ` ${win.document.visibilityState}`
        : '';
    events.push(
      `${event.type}${persisted}${hidden}@${Math.round(
        (win.performance?.now() ?? 0) - start,
      )}ms`,
    );
  };
  const listen = method => {
    WINDOW_LIFECYCLE_EVENTS.forEach(type => win[method]?.(type, record));
    DOCUMENT_LIFECYCLE_EVENTS.forEach(type =>
      win.document[method]?.(type, record),
    );
  };
  listen('addEventListener');
  return { events, stop: () => listen('removeEventListener') };
};

// The report waits for the retry, and today's check is already recorded, so
// a retry that stalls must not hold the report back for long: a navigation
// in the meantime would lose it.
const RETRY_TIMEOUT_MS = 2000;

const headOwnPage = (win, signal) =>
  win.fetch(win.location.origin + win.location.pathname, {
    method: 'HEAD',
    cache: 'no-store',
    credentials: 'same-origin',
    ...(signal && { signal }),
  });

// Decides from the response to a HEAD request for our own page whether our
// frame-ancestors policy allows these ancestors.
const decide = (win, ancestors, response) => {
  try {
    const csp = response.headers.get('Content-Security-Policy');
    const reportOnly = response.headers.get(
      'Content-Security-Policy-Report-Only',
    );
    const headers = `status ${response.status}, csp ${
      csp ? 'present' : 'missing'
    }, report-only ${reportOnly ? 'present' : 'missing'}`;

    if (!frameAncestorsSourceLists([csp, reportOnly]).length) {
      return { allowed: false, check: `no-frame-ancestors (${headers})` };
    }
    const allowed = isFramingAllowed(
      [csp, reportOnly],
      ancestors,
      win.location.origin,
    );
    return {
      allowed,
      check: `${allowed ? 'allowed' : 'not-allowed'} (${headers})`,
    };
  } catch (error) {
    return { allowed: false, check: `check-threw (${errorMessage(error)})` };
  }
};

// A page's script can't read the headers its own document arrived with, so
// ask for them again. Any failure counts as "not allowed", so we report.
//
// Returns whether framing is allowed, plus a short description of how that
// was decided. The description goes into the report, so a report from an
// allowed embedder explains itself (see sc-370021: allowlisted merchants
// still being reported, almost all from Mobile Safari).
//
// When the HEAD request fails, it is retried once, immediately, and a retry
// that gets a response decides. The first attempt fails on WebKit, fast and
// with the page visible, on what looks like a dead pooled connection; in the
// v0.0.304 reports, 13 of 14 retries came back allowed (sc-370021). If the
// result is still a report, `headFailure` carries the timing, the visibility
// and network state at the first failure, and the retry's outcome, for the
// report to describe.
const checkOwnPolicy = async (win, ancestors, start) => {
  if (!ancestors.length) return { allowed: false, check: 'no-ancestors' };

  const now = () => win.performance?.now() ?? 0;

  let response;
  try {
    response = await headOwnPage(win);
  } catch (error) {
    const failedAt = now();
    const headFailure = {
      failedAfter: Math.round(failedAt - start),
      visibility: win.document.visibilityState ?? 'unknown',
      online: win.navigator?.onLine ?? 'unknown',
      connection: win.navigator?.connection?.effectiveType ?? 'unknown',
    };

    let decision;
    let retry;
    let timeoutId;
    try {
      // AbortController rather than AbortSignal.timeout, which is too new
      // for our browserslist floor (see util/config.js).
      const controller = new AbortController();
      timeoutId = setTimeout(() => controller.abort(), RETRY_TIMEOUT_MS);
      decision = decide(
        win,
        ancestors,
        await headOwnPage(win, controller.signal),
      );
      retry = decision.check;
    } catch (retryError) {
      retry =
        retryError?.name === 'AbortError'
          ? 'timed-out'
          : `head-failed (${errorMessage(retryError)})`;
    } finally {
      clearTimeout(timeoutId);
    }
    headFailure.retry = `${retry} after ${Math.round(now() - failedAt)}ms`;

    if (decision) return { ...decision, headFailure };
    return {
      allowed: false,
      check: `head-failed (${errorMessage(error)})`,
      headFailure,
    };
  }

  return decide(win, ancestors, response);
};

/**
 * Reports, at most once per embedding site per browser per day, that this
 * document is rendered inside a frame, and by which site.
 *
 * Browser-generated CSP frame-ancestors reports only identify our own page,
 * never the embedding site, so they cannot tell us what to put in the
 * frame-ancestors allowlist. This sends a CSP-format report of its own to the
 * same Sentry project, with the embedding origin as the blocked URI, so each
 * embedding site groups into its own issue: "Blocked 'frame-ancestors' from
 * '<embedding site>'". See sc-370021.
 *
 * Embedding sites that our own frame-ancestors directive already allows (in
 * either the enforced or the report-only CSP) are not reported, so once the
 * allowlist exists, only sites missing from it show up.
 *
 * It deliberately does not use the Sentry SDK, which is gated to a small
 * share of clients to control cost; this has to run for every client. It has
 * its own kill switch instead: see components/frame-detection.
 *
 * `win` is injectable for tests.
 */
export const reportIfFramed = async (win = window) => {
  if (win.self === win.top || win.Cypress) return;

  // Nearest ancestor first, top-level page last. Not supported in Firefox,
  // where we fall back to the referrer, which identifies the parent only.
  const ancestorOrigins = Array.from(win.location.ancestorOrigins ?? []);
  const referrerOrigin = originOf(win.document.referrer);
  const parentOrigin = ancestorOrigins[0] ?? referrerOrigin ?? 'unknown';

  const today = new Date().toISOString().slice(0, 10);
  if (!isFirstReportToday(win.localStorage, parentOrigin, today)) return;

  const knownAncestors = ancestorOrigins.length
    ? ancestorOrigins
    : [referrerOrigin].filter(Boolean);

  const start = win.performance?.now() ?? 0;
  const lifecycle = watchLifecycle(win, start);
  const { allowed, check, headFailure } = await checkOwnPolicy(
    win,
    knownAncestors,
    start,
  );
  if (allowed) {
    lifecycle.stop();
    return;
  }

  // For a failed HEAD request: when it started relative to page load, how
  // long until it failed, whether the page was visible and online then, what
  // the retry found, and every lifecycle event up to now (when the report is
  // sent).
  const detail = headFailure
    ? `; started ${Math.round(start)}ms after load, failed after ${
        headFailure.failedAfter
      }ms, visibility ${headFailure.visibility}, online ${
        headFailure.online
      }, connection ${headFailure.connection}, retry: ${
        headFailure.retry
      }, reported after ${Math.round(
        (win.performance?.now() ?? 0) - start,
      )}ms, lifecycle: ${lifecycle.events.join(', ') || 'none'}`
    : '';
  lifecycle.stop();

  const environment =
    getEnv('REACT_APP_SENTRY_ENVIRONMENT') || getEnv('REACT_APP_ENV_NAME');
  const release = getEnv('REACT_APP_SENTRY_RELEASE') || 'unknown';

  const body = JSON.stringify({
    'csp-report': {
      // Origin and path only: the query string can carry tokens.
      'document-uri': win.location.origin + win.location.pathname,
      referrer: win.document.referrer,
      'blocked-uri': parentOrigin,
      'violated-directive': 'frame-ancestors',
      'effective-directive': 'frame-ancestors',
      // Diagnostics, carried in the one free-text field Sentry shows
      // prominently for CSP reports.
      'original-policy': `frame-ancestors (in-app frame detection; release: ${release}; check: ${check}${detail}; ancestors: ${
        ancestorOrigins.join(' ') || 'unavailable'
      })`,
      disposition: 'report',
    },
  });

  await win
    .fetch(
      `${CSP_REPORT_ENDPOINT}&sentry_environment=${encodeURIComponent(
        environment ?? '',
      )}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/csp-report' },
        body,
        credentials: 'omit',
        keepalive: true,
      },
    )
    .catch(() => {});
};
